import { createFileRoute, useNavigate, redirect } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { store, useStore, type Sale } from "@/lib/pos-store";
import { PageHeader, fmt } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search,
  Minus,
  Plus,
  Trash2,
  ReceiptText,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { hasRole, useAuth } from "@/lib/auth-store";
import { tabStore, useCurrentTab } from "@/lib/tab-store";

export const Route = createFileRoute("/sell")({
  beforeLoad: ({ context }) => {
    if (!hasRole(context.user, "viewer")) {
      throw redirect({ to: "/" });
    }
  },
  head: () => ({
    meta: [
      { title: "New Sale" },
      { name: "description", content: "Record a sale and print a receipt." },
    ],
  }),
  component: SellPage,
});

type Line = { itemId: string; qty: number };

function SellPage() {
  const { user } = useAuth();
  const items = useStore((s) => s.items);
  const currentTab = useCurrentTab();
  const isViewer = user?.role === "viewer";
  const [q, setQ] = useState("");
  const [cart, setCart] = useState<Line[]>([]);
  const [payment, setPayment] = useState<Sale["payment"]>("cash");
  const navigate = useNavigate();

  const filtered = useMemo(
    () =>
      items.filter(
        (i) =>
          i.name.toLowerCase().includes(q.toLowerCase()) ||
          i.sku.toLowerCase().includes(q.toLowerCase()),
      ),
    [items, q],
  );

  const addToCart = (id: string) => {
    setCart((c) => {
      const ex = c.find((l) => l.itemId === id);
      if (ex)
        return c.map((l) => (l.itemId === id ? { ...l, qty: l.qty + 1 } : l));
      return [...c, { itemId: id, qty: 1 }];
    });
  };

  const total = cart.reduce((a, l) => {
    const it = items.find((i) => i.id === l.itemId)!;
    return a + it.sellingPrice * l.qty;
  }, 0);

  const checkout = async () => {
    if (cart.length === 0) return;
    for (const l of cart) {
      const it = items.find((i) => i.id === l.itemId)!;
      if (it.stock < l.qty) {
        toast.error(`Insufficient stock for ${it.name}`);
        return;
      }
    }
    const firstSaleId = await store.checkout(cart, payment);
    if (!firstSaleId) {
      toast.error("Checkout failed");
      return;
    }
    toast.success(`Sale completed · ${fmt(total)}`);
    setCart([]);
    navigate({ to: "/receipt/$id", params: { id: firstSaleId } });
  };

  const addToTab = async () => {
    if (cart.length === 0) return;
    for (const l of cart) {
      const it = items.find((i) => i.id === l.itemId)!;
      await tabStore.addToTab(l.itemId, it.name, l.qty, it.sellingPrice);
    }
    toast.success(`Added to your tab · ${fmt(total)}`);
    setCart([]);
    navigate({ to: "/tab" });
  };

  return (
    <div>
      <PageHeader
        title={isViewer ? "Order Drinks" : "New Sale"}
        subtitle="Tap items to add"
      />

      {/* Tab summary bar for viewers with open tab */}
      {isViewer && currentTab && currentTab.items.length > 0 && (
        <button
          onClick={() => navigate({ to: "/tab" })}
          className="mx-4 mt-3 flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 p-3 text-left active:scale-[0.98]"
        >
          <ReceiptText className="h-5 w-5 text-primary" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">Your Tab</p>
            <p className="text-xs text-muted-foreground">
              {currentTab.items.reduce((a, i) => a + i.qty, 0)} item
              {currentTab.items.reduce((a, i) => a + i.qty, 0) !== 1
                ? "s"
                : ""}{" "}
              · {fmt(currentTab.items.reduce((a, i) => a + i.total, 0))}
            </p>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </button>
      )}

      <div className="px-4 pt-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search item or SKU"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 px-4 pt-4">
        {filtered.map((it) => (
          <button
            key={it.id}
            onClick={() => addToCart(it.id)}
            disabled={it.stock <= 0}
            className="rounded-xl border border-border bg-card p-3 text-left active:scale-[0.97] disabled:opacity-40 min-h-[72px]"
          >
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {it.sku}
            </p>
            <p className="line-clamp-2 text-sm font-medium leading-snug">
              {it.name}
            </p>
            <div className="mt-2 flex items-center justify-between">
              <span className="font-display text-sm font-semibold text-primary">
                {fmt(it.sellingPrice)}
              </span>
              <span className="text-xs text-muted-foreground">
                {it.stock} left
              </span>
            </div>
          </button>
        ))}
      </div>

      {cart.length > 0 && (
        <div className="fixed inset-x-0 bottom-16 z-30 mx-auto max-w-md border-t border-border bg-card p-3 shadow-2xl">
          <div className="max-h-44 space-y-1.5 overflow-y-auto">
            {cart.map((l) => {
              const it = items.find((i) => i.id === l.itemId)!;
              return (
                <div key={l.itemId} className="flex items-center gap-2">
                  <p className="flex-1 truncate text-sm font-medium">
                    {it.name}
                  </p>
                  <button
                    onClick={() =>
                      setCart((c) =>
                        c
                          .map((x) =>
                            x.itemId === l.itemId
                              ? { ...x, qty: Math.max(0, x.qty - 1) }
                              : x,
                          )
                          .filter((x) => x.qty > 0),
                      )
                    }
                    className="rounded-lg bg-secondary p-2 min-h-[40px] min-w-[40px] flex items-center justify-center active:scale-95"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="w-8 text-center text-sm font-bold tabular-nums">
                    {l.qty}
                  </span>
                  <button
                    onClick={() => addToCart(l.itemId)}
                    className="rounded-lg bg-secondary p-2 min-h-[40px] min-w-[40px] flex items-center justify-center active:scale-95"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <span className="w-16 text-right text-sm font-semibold tabular-nums">
                    {fmt(it.sellingPrice * l.qty)}
                  </span>
                  <button
                    onClick={() =>
                      setCart((c) => c.filter((x) => x.itemId !== l.itemId))
                    }
                    className="rounded-lg p-2 text-muted-foreground min-h-[40px] min-w-[40px] flex items-center justify-center active:scale-95"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>

          {isViewer ? (
            <Button className="mt-2 w-full" size="lg" onClick={addToTab}>
              <ReceiptText className="h-4 w-4" /> Add to Tab {fmt(total)}
            </Button>
          ) : (
            <>
              <div className="mt-3 flex gap-1.5">
                {(["cash", "mpesa", "bank"] as const).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPayment(p)}
                    className={`flex-1 rounded-lg px-3 py-2.5 text-sm font-medium capitalize min-h-[44px] active:scale-95 transition ${
                      payment === p
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary text-secondary-foreground"
                    }`}
                  >
                    {p === "mpesa" ? "M-Pesa" : p}
                  </button>
                ))}
              </div>
              <Button className="mt-2 w-full" size="lg" onClick={checkout}>
                Charge {fmt(total)}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
